import {
  createLibraryRecord,
  deleteLibraryRecord,
  fetchLibraryFields,
  fetchLibraryRecord,
  fetchLibraryRecords,
  patchLibraryRecord,
} from '@/features/metadata-library/api/metadataLibrary';
import {
  METADATA_LIBRARY_FIELDS_STALE_TIME,
  METADATA_LIBRARY_PAGE_LIMIT,
  METADATA_LIBRARY_STALE_TIME,
} from '@/features/metadata-library/config/constants';
import { venueKeys } from '@/features/metadata-library/config/venueKeys';
import { venueFormDefinition } from '@/features/metadata-library/lib/venueForm';
import type {
  LibraryCreateInputMap,
  VenueSnapshot,
} from '@/features/metadata-library/types/libraryRequests';
import type {
  LibraryRecordFilters,
  LibraryRecordsPage,
} from '@/features/metadata-library/types/metadataLibrary';
import type { SupportedLocale } from '@/shared/types/i18n';
import {
  type QueryClient,
  type QueryFunctionContext,
  type QueryKey,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export function venueFieldsQuery(locale: SupportedLocale) {
  return queryOptions({
    queryKey: venueKeys.fields(locale),
    queryFn: ({ signal }) => fetchLibraryFields('environments', locale, { signal }),
    staleTime: METADATA_LIBRARY_FIELDS_STALE_TIME,
  });
}

export function useVenueFields(locale: SupportedLocale) {
  return useQuery({ ...venueFieldsQuery(locale), select: venueFormDefinition });
}

export function venueRecordsQuery(q?: string, organizationIds?: readonly string[]) {
  const term = q?.trim() || undefined;

  return {
    queryKey: venueKeys.recordsList(term, organizationIds),
    queryFn: ({ pageParam, signal }: QueryFunctionContext<QueryKey, string | undefined>) =>
      fetchLibraryRecords(
        'environments',
        {
          q: term,
          organizationId: organizationIds,
          cursor: pageParam,
          limit: METADATA_LIBRARY_PAGE_LIMIT,
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: LibraryRecordsPage) =>
      last.items.length === 0 ? undefined : (last.nextCursor ?? undefined),
    staleTime: METADATA_LIBRARY_STALE_TIME,
  };
}

export function useVenueRecordOptions(
  { q, organizationIds }: LibraryRecordFilters,
  enabled: boolean,
) {
  return useInfiniteQuery({
    ...venueRecordsQuery(q, organizationIds),
    enabled,
    throwOnError: false,
  });
}

export function venueRecordQuery(recordId: string) {
  return {
    queryKey: venueKeys.record(recordId),
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchLibraryRecord('environments', recordId, { signal }),
    enabled: Boolean(recordId),
    staleTime: METADATA_LIBRARY_STALE_TIME,
    retry: false,
  };
}

export function useVenueRecord(recordId: string) {
  return useQuery(venueRecordQuery(recordId));
}

export function useCreateVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LibraryCreateInputMap['environments']) =>
      createLibraryRecord('environments', body),
    onSuccess: (record) => {
      queryClient.setQueryData(venueKeys.record(record.environmentId), record);
      void queryClient.invalidateQueries({ queryKey: venueKeys.records });
    },
  });
}

export function usePatchVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ recordId, body }: { recordId: string; body: VenueSnapshot }) =>
      patchLibraryRecord('environments', recordId, body),
    onSuccess: (record, variables) => {
      queryClient.setQueryData(venueKeys.record(variables.recordId), record);
      void queryClient.invalidateQueries({ queryKey: venueKeys.records });
    },
  });
}

export function venueDeleteMutation(queryClient: QueryClient) {
  return {
    mutationFn: (recordId: string) => deleteLibraryRecord('environments', recordId),
    onSuccess: (_result: unknown, recordId: string) => {
      void queryClient.invalidateQueries({ queryKey: venueKeys.record(recordId), exact: true });
      void queryClient.invalidateQueries({ queryKey: venueKeys.records });
    },
  };
}
